import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import styles from './Toast.module.css';

export type ToastTone = 'success' | 'danger' | 'info';

export interface ToastAction {
  /** Short verb, e.g. "Rückgängig" or "Ansehen". */
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  action?: ToastAction | undefined;
  /** Dismissed; removed once the exit animation has finished. */
  leaving?: boolean;
}

interface ToastApi {
  /** Shows a short confirmation; at most one action (docs/ux-richtlinien.md §6.11). */
  show: (message: string, tone?: ToastTone, action?: ToastAction) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export interface ToastProviderProps {
  /** Accessible label of the dismiss button (i18n). */
  dismissLabel: string;
  /** Milliseconds until a toast disappears; doubled when it has an action. */
  duration?: number;
  children: ReactNode;
}

function ToastView({
  item,
  duration,
  dismissLabel,
  onDismiss,
  onRemove,
}: {
  item: ToastItem;
  duration: number;
  dismissLabel: string;
  onDismiss: () => void;
  onRemove: (id: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused || item.leaving) return;
    const timer = window.setTimeout(onDismiss, item.action ? duration * 2 : duration);
    return () => window.clearTimeout(timer);
  }, [paused, duration, item.action, item.leaving, onDismiss]);

  // Leaving: wait for the exit animation from the CSS, then remove.
  useEffect(() => {
    if (!item.leaving) return;
    const el = ref.current;
    const animations = el && typeof el.getAnimations === 'function' ? el.getAnimations() : [];
    if (animations.length === 0) {
      onRemove(item.id);
      return;
    }
    let active = true;
    void Promise.allSettled(animations.map((a) => a.finished)).then(() => {
      if (active) onRemove(item.id);
    });
    return () => {
      active = false;
    };
  }, [item.leaving, item.id, onRemove]);

  // Swipe sideways to dismiss (phones); the toast follows the finger.
  const swipe = useRef<{ x: number; t: number; id: number } | null>(null);
  const setX = (px: number) => ref.current?.style.setProperty('--toast-x', `${px}px`);

  return (
    <div
      ref={ref}
      className={`${styles.toast} ${item.leaving ? styles.leaving : ''}`}
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        swipe.current = { x: e.clientX, t: e.timeStamp, id: e.pointerId };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.currentTarget.style.transition = 'none';
      }}
      onPointerMove={(e) => {
        if (swipe.current?.id === e.pointerId) setX(e.clientX - swipe.current.x);
      }}
      onPointerUp={(e) => {
        const s = swipe.current;
        if (!s || s.id !== e.pointerId) return;
        swipe.current = null;
        e.currentTarget.style.transition = '';
        const dx = e.clientX - s.x;
        const velocity = Math.abs(dx) / Math.max(1, e.timeStamp - s.t);
        if (Math.abs(dx) > 80 || (Math.abs(dx) > 20 && velocity > 0.5)) onDismiss();
        else setX(0);
      }}
      onPointerCancel={(e) => {
        swipe.current = null;
        e.currentTarget.style.transition = '';
        setX(0);
      }}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {item.tone === 'danger' && <Icon name="alert-circle" size="sm" className={styles.danger} />}
      <span className={styles.message}>{item.message}</span>
      {item.action && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            item.action?.onClick();
            onDismiss();
          }}
        >
          {item.action.label}
        </button>
      )}
      <Button
        variant="ghost"
        size="xs"
        aria-label={dismissLabel}
        onClick={onDismiss}
        iconStart={<Icon name="close" size="sm" />}
        className={styles.close}
      />
    </div>
  );
}

/** Short confirmations ("Gespeichert") in a polite live region. */
export function ToastProvider({ dismissLabel, duration = 4000, children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const dismiss = useCallback(
    (id: number) => setToasts((t) => t.map((x) => (x.id === id ? { ...x, leaving: true } : x))),
    [],
  );
  const show = useCallback((message: string, tone: ToastTone = 'success', action?: ToastAction) => {
    const id = nextId.current++;
    setToasts((t) => {
      // At most three visible: older ones leave with their exit animation.
      const visible = t.filter((x) => !x.leaving);
      const surplus = new Set(visible.slice(0, -2).map((x) => x.id));
      return [
        ...t.map((x) => (surplus.has(x.id) ? { ...x, leaving: true } : x)),
        { id, tone, message, action },
      ];
    });
  }, []);
  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.region} role="status" aria-live="polite">
        {toasts.map((t) => (
          <ToastView
            key={t.id}
            item={t}
            duration={duration}
            dismissLabel={dismissLabel}
            onDismiss={() => dismiss(t.id)}
            onRemove={remove}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}
